import { useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Shovel, Hammer, HardHat, Truck, Mountain, ChevronLeft, ChevronRight } from "lucide-react";
import { SERVICES } from "@/lib/site";
import Reveal from "@/components/layout/Reveal";

const ICONS = { Shovel, Hammer, HardHat, Truck, Mountain };

function ServiceCard({ s, i, dark, wide }) {
    const Icon = ICONS[s.icon] || Shovel;
    return (
        <Link
            to={`/prestations/${s.slug}`}
            data-testid="service-card"
            className={`group relative flex flex-col h-full rounded-md border overflow-hidden card-elevate transition ${
                wide ? "w-[85vw] sm:w-[420px] lg:w-full flex-shrink-0 snap-start" : ""
            } ${
                dark
                    ? "bg-white/[0.04] border-white/10 hover:border-white/40 text-white"
                    : "bg-white border-concrete-200 hover:border-navy-700 text-navy-700"
            }`}
        >
            {/* Image */}
            <div className="relative aspect-[16/10] overflow-hidden">
                <img
                    src={s.image}
                    alt={s.title}
                    loading="lazy"
                    className="w-full h-full object-cover transition duration-700 group-hover:scale-105"
                />
                <div
                    className={`absolute inset-0 ${
                        dark ? "bg-navy-900/40" : "bg-navy-900/25"
                    } group-hover:bg-navy-900/15 transition`}
                />
                <div className="absolute top-3 left-3 bg-white/95 backdrop-blur px-2.5 py-1 rounded font-mono-num text-[11px] text-navy-700">
                    {String(i + 1).padStart(2, "0")}
                </div>
                <div className="absolute bottom-3 right-3 w-10 h-10 rounded-md bg-white/95 backdrop-blur text-navy-700 flex items-center justify-center">
                    <Icon className="w-5 h-5" strokeWidth={1.5} />
                </div>
            </div>

            {/* Contenu */}
            <div className="p-6 flex flex-col flex-1">
                <h3 className="font-heading text-lg md:text-xl font-semibold leading-tight tracking-tight">
                    {s.title}
                </h3>
                <p
                    className={`mt-2 text-sm leading-relaxed ${
                        dark ? "text-white/70" : "text-navy-700/70"
                    }`}
                >
                    {s.short}
                </p>
                <div
                    className={`mt-6 inline-flex items-center gap-2 text-sm font-semibold ${
                        dark ? "text-white" : "text-navy-700"
                    }`}
                >
                    Découvrir
                    <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition" />
                </div>
            </div>
        </Link>
    );
}

/**
 * ServicesGrid
 * - layout="grid" (default) : grille classique responsive
 * - layout="carousel"       : bandeau horizontal déroulant, 4 en 1 ligne sur desktop (lg+),
 *                             scroll horizontal snap sur tablette/mobile.
 */
export default function ServicesGrid({ dark = false, layout = "grid" }) {
    if (layout === "carousel") {
        return <ServicesCarousel dark={dark} />;
    }
    return (
        <div
            data-testid="services-grid"
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5"
        >
            {SERVICES.map((s, i) => (
                <Reveal key={s.slug} delay={i * 0.05}>
                    <ServiceCard s={s} i={i} dark={dark} />
                </Reveal>
            ))}
        </div>
    );
}

function ServicesCarousel({ dark }) {
    const scrollerRef = useRef(null);
    const [canPrev, setCanPrev] = useState(false);
    const [canNext, setCanNext] = useState(true);

    const updateNav = () => {
        const el = scrollerRef.current;
        if (!el) return;
        setCanPrev(el.scrollLeft > 8);
        setCanNext(el.scrollLeft + el.clientWidth < el.scrollWidth - 8);
    };

    useEffect(() => {
        updateNav();
        const el = scrollerRef.current;
        if (!el) return;
        el.addEventListener("scroll", updateNav, { passive: true });
        window.addEventListener("resize", updateNav);
        return () => {
            el.removeEventListener("scroll", updateNav);
            window.removeEventListener("resize", updateNav);
        };
    }, []);

    const scrollBy = (dir) => {
        const el = scrollerRef.current;
        if (!el) return;
        const card = el.querySelector("[data-testid=service-card]");
        const step = card ? card.getBoundingClientRect().width + 20 : el.clientWidth * 0.8;
        el.scrollBy({ left: dir * step, behavior: "smooth" });
    };

    return (
        <div className="relative" data-testid="services-carousel">
            {/* Flèches nav (masquées sur lg si tout tient) */}
            <div className="hidden md:flex lg:hidden absolute -top-16 right-0 gap-2 z-10">
                <button
                    type="button"
                    aria-label="Précédent"
                    onClick={() => scrollBy(-1)}
                    disabled={!canPrev}
                    data-testid="carousel-prev"
                    className={`w-10 h-10 rounded-md border flex items-center justify-center transition ${
                        canPrev
                            ? "bg-white border-concrete-200 text-navy-700 hover:border-navy-700 hover:bg-concrete-50"
                            : "bg-concrete-50 border-concrete-100 text-navy-700/30 cursor-not-allowed"
                    }`}
                >
                    <ChevronLeft className="w-5 h-5" />
                </button>
                <button
                    type="button"
                    aria-label="Suivant"
                    onClick={() => scrollBy(1)}
                    disabled={!canNext}
                    data-testid="carousel-next"
                    className={`w-10 h-10 rounded-md border flex items-center justify-center transition ${
                        canNext
                            ? "bg-white border-concrete-200 text-navy-700 hover:border-navy-700 hover:bg-concrete-50"
                            : "bg-concrete-50 border-concrete-100 text-navy-700/30 cursor-not-allowed"
                    }`}
                >
                    <ChevronRight className="w-5 h-5" />
                </button>
            </div>

            <div
                ref={scrollerRef}
                className="flex lg:grid lg:grid-cols-4 gap-5 overflow-x-auto lg:overflow-visible snap-x snap-mandatory pb-4 lg:pb-0 -mx-4 sm:-mx-6 lg:mx-0 px-4 sm:px-6 lg:px-0 scroll-smooth [scrollbar-width:thin]"
                data-testid="services-grid"
            >
                {SERVICES.map((s, i) => (
                    <Reveal key={s.slug} delay={i * 0.04} className="lg:contents">
                        <ServiceCard s={s} i={i} dark={dark} wide />
                    </Reveal>
                ))}
            </div>

            {/* Indicateur de scroll pour mobile */}
            <p className="lg:hidden mt-3 text-xs text-navy-700/60 flex items-center gap-2">
                <span className="h-px flex-1 bg-concrete-200" />
                Faites défiler pour découvrir les 4 domaines d'intervention
                <ChevronRight className="w-3.5 h-3.5" />
            </p>
        </div>
    );
}
